import axios from 'axios';
import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source';

const getResponseErrorMessage = async (response) => {
  const fallbackMessage = `Request failed with status ${response.status}`;

  try {
    const responseText = await response.text();
    if (!responseText) return fallbackMessage;

    try {
      const responseData = JSON.parse(responseText);
      return responseData.message || responseData.error || responseText;
    } catch {
      return responseText;
    }
  } catch {
    return fallbackMessage;
  }
};

const parsePredictedProgramsStreamData = (data) => {
  const responseData = JSON.parse(data);

  if (responseData?.error) {
    throw new Error(responseData.error);
  }

  if (!Array.isArray(responseData?.result)) {
    throw new Error('The predicted programs stream returned an invalid response.');
  }

  return responseData.result;
};

const parsePredictedProgramsStreamError = (data) => {
  const fallbackMessage = 'Failed to generate predicted programs.';

  if (!data?.trim()) return fallbackMessage;

  try {
    const responseData = JSON.parse(data);

    if (typeof responseData === 'string') {
      return responseData || fallbackMessage;
    }

    return responseData?.error || responseData?.message || fallbackMessage;
  } catch {
    return data;
  }
};

const getAllPrograms = () => {
  return axios.get('/api/programs');
};

const getAllProgramsByBudgetId = (budgetId, itemId) => {
  return axios.get('/api/programs/getAllProgramsByBudgetId', {
    params: {
      budgetId: budgetId,
      itemId: itemId,
    },
  });
};

const savePrograms = (programs) => {
  return axios.post('/api/programs/addOrEditAll', programs);
};

const getAllServiceTypes = (config) => {
  return axios.get('/api/service-types', config);
};

const getDefaultServiceTypeBoundaries = (serviceTypeId, config) => {
  return axios.get(
    `/api/service-types/defaultQuartileBoundaries/${serviceTypeId}`,
    config
  );
};

const adjustServiceTypeBoundaries = (serviceTypes, config) => {
  return axios.put('/api/service-types', { serviceTypes }, config);
};

const deletePrograms = (ids) => {
  return axios.delete('/api/programs/delete-programs', { data: { ids } });
};

const uploadProgram = (program) => {
  return axios.post('/api/programs/upload', program);
};

const advancedSearchByKeyword = (keyword) => {
  return axios.get('/api/programs/advancedSearch', {
    params: {
      keyword: keyword,
    },
  });
};

const combinePrograms = (mainProgram, programs, costCenter) => {
  return axios.post('/api/programs/combine', {
    mainProgram: mainProgram,
    programs: programs,
    costCenter: costCenter,
  });
};

const getProgramCountForBudget = () => {
  return axios.get('/api/programs/program-count-for-budget');
};

const filterByActiveCostCenters = (data, user) => {;
  if (
    user.activeCostCenters.length > 0 &&
    user.activeCostCenters.length !== user.costCenters.length
  ) {
    return data.filter((program) =>
      user.activeCostCenters.find(
        (acc) => acc === program.costCenter.name.trim()
      )
    );
  }
  return data;
};

const filterByUserAssignedCostCenters = (programs, user) => {
  let userCostCenters = user.costCenters;
  let userFilteredPrograms = [];
  for (let i = 0; i < programs.length; i++) {
    for (let j = 0; j < userCostCenters.length; j++) {
      if (programs[i].costCenter.name === userCostCenters[j]) {
        userFilteredPrograms.push(programs[i]);
      }
    }
  }
  return userFilteredPrograms;
};

const getAdminPrograms = (departments) => {
  return axios.get('/api/admin/getAdminPrograms', {
    params: {
      departments: departments,
    },
  });
};

const getProgramDepartments = () => {
  return axios.get('/api/admin/getAdminProgramDepartments');
};

const getOrganizationTypes = () => {
  return axios.get('/api/admin/getAdminOrganizationTypes');
};

const getDepartmentsAndDivisions = (body) => {
  return axios.post('/api/programs/getDepartmentsAndDivisions', body);
};

const filterByResultScore = (selectedOptions, program) => {
  const resultType = program.resultTypes.find(
    (programResultType) => programResultType.name === program.serviceType.name
  );
  let match = [];
  if (resultType) {
    for (const selectedResult of selectedOptions) {
      const programResult = resultType.results.find(
        (result) => result.abbreviation === selectedResult.name
      );
      const finalScore =
        programResult &&
        programResult.scores.find((score) =>
          (score.scoreTypeId === 3 ||
            score.scoreTypeId === 1 ||
            score.scoreTypeId === 2) &&
            score.scoreOption &&
            score.scoreOption.name
            ? selectedResult.value.find(
              (option) => option.name === score.scoreOption.name
            )
            : selectedResult.value.find(
              (option) => option.id === score.scoreOptionId
            )
        );
      if (finalScore) {
        match.push(finalScore);
      }
    }
  }

  return match.length >= 1;
};

const filterByBPAScore = (selectedOptions, program, scoreTypes) => {
  let isIncluded = [];
  let BPAResultType = program.resultTypes.find(
    (resultType) => resultType.name === 'BPA'
  );
  if (!BPAResultType) return false;

  for (const selectedResult of selectedOptions) {
    let resultInProgram = BPAResultType.results.find(
      (result) => result.abbreviation === selectedResult.name
    );
    if (!resultInProgram) return false;
    let hasScore = filterSingleProgramByResult(
      resultInProgram,
      isIncluded,
      selectedResult,
      scoreTypes
    );
    if (!hasScore) return hasScore;
  }

  return isIncluded.every((element) => element === true);
};

const filterSingleProgramByResult = (
  resultInProgram,
  isIncluded,
  selectedResult,
  scoreTypes
) => {
  const scores = resultInProgram.scores.filter((score) =>
    scoreTypes.includes(score.scoreTypeId)
  );
  if (scores.length <= 0) return false;
  isIncluded.push(
    scores.some((score) =>
      selectedResult.value.find(
        (selectedOption) => selectedOption.id === score.scoreOptionId
      )
    )
  );
  return true;
};

const getProgramsWithScores = (scoreType) => {
  return new Promise((resolve) => {
    axios
      .get('/api/programs/programsWithScores' + '?scoreType=' + scoreType)
      .then((response) => {
        resolve(response);
      });
  });
};

const getTeamReconciliationPrograms = (config) => {
  return axios.get('/api/programs/programWithDiffPeerScores', config);
};

const getProgramWithScoringInfo = (programInfo, config) => {
  return axios.get('/api/programs/singleProgramWithScoringInfo', {
    params: {
      scoreType: 'Peer',
      programId: programInfo.programId,
      resultIds: programInfo.resultIds,
    },
    ...config,
  });
};

const getDepartmentReconciliationPrograms = (config) => {
  return axios.get('/api/programs/programWithDiffDeptAndPeerScore', config);
};

const getFinalReviewPrograms = () => {
  return axios.get('/api/programs/finalReviewPrograms');
};

const getProgramScoresByResult = (id) => {
  return axios.get('/api/programs/programScoresByResult/' + id);
};

const getProgramsForInsightsTable = (filteringInput, programId) => {
  return axios.post('/api/programs/programsForInsightsTable', {
    filteringInput,
    programId
  });
};

const getFullProgramInfo = (programIds) => {
  return axios.post('/api/programs/programsInfo', {
    programIds: programIds,
  });
};

const getScoredServiceTypePrograms = () => {
  return axios.get('/api/programs/scoredServiceTypePrograms');
};

const getCostDetails = (id) => {
  return axios.get('/api/programs/cost-details', {
    params: {
      id,
    },
  });
};

const getScoringPredictions = (programs) => {
  return axios.post('/api/programs/get-scoring-predictions', programs);
};

const getRxAdminScoringPredictions = (body, config) => {
  return axios.post('/api/programs/get-rxadmin-scoring-predictions', body, config);
};

const getPredictedPrograms = async (
  website,
  numberOfPrograms,
  { activeCostCenterIds = [], signal, onPrograms } = {}
) => {
  const queryParams = new URLSearchParams({
    'payload[website]': website,
    'payload[num_of_progs]': String(numberOfPrograms),
  });

  (activeCostCenterIds || []).forEach((costCenterId) => {
    queryParams.append('activeCostCenterIds', String(costCenterId));
  });

  const predictedPrograms = [];
  let receivedCompletionEvent = false;

  await fetchEventSource(
    `/api/programs/get-predicted-programs?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: {
        accept: EventStreamContentType,
      },
      signal,
      openWhenHidden: true,
      async onopen(response) {
        if (!response.ok) {
          throw new Error(await getResponseErrorMessage(response));
        }

        const contentType = response.headers.get('content-type');
        if (!contentType?.startsWith(EventStreamContentType)) {
          throw new Error(
            `Expected content-type to be ${EventStreamContentType}, actual: ${contentType}`
          );
        }
      },
      onmessage(message) {
        if (message.event === 'complete') {
          receivedCompletionEvent = true;
          return;
        }

        if (message.event === 'error') {
          throw new Error(parsePredictedProgramsStreamError(message.data));
        }

        if (message.event && message.event !== 'message') return;
        if (!message.data) return;

        const programs = parsePredictedProgramsStreamData(message.data);
        predictedPrograms.push(...programs);
        onPrograms?.(programs);
      },
      onclose() {
        if (!receivedCompletionEvent) {
          throw new Error('The predicted programs stream closed before completion.');
        }
      },
      onerror(error) {
        throw error;
      },
    }
  );

  if (signal?.aborted) {
    const error = new Error('The predicted programs request was aborted.');
    error.name = 'AbortError';
    throw error;
  }

  return { data: predictedPrograms };
};
const finalizeRxAdminScores = () => {
  return axios.post('/api/programs/finalize-rxadmin-scores');
};

const analyzePrograms = (requestBody) => {
  return axios.post('/api/programs/program-process-overlap', requestBody);
};

const getCostBenchmarkAnalysis = (benchmarkType) => {
  return axios.post('/api/programs/benchmark-analysis', { benchmarkType });
};

const getSimilarPrograms = (programIds) => {
  return axios.get('/api/programs/similar-programs', {
    params: { programIds: programIds?.join(',') }
  });
};

const getAIAnalysis = (filters, filteredData) => {
  return axios.post('/api/programs/benchmark-ai-insights', {
    filters,
    filteredData
  });
};

const getProgramInfoForInsightPrediction = (budgetId) => {
  return axios.post('/api/programs/programs-cost-details', {budgetId});
};

export {
  getAllPrograms,
  getAllProgramsByBudgetId,
  savePrograms,
  getAllServiceTypes,
  deletePrograms,
  uploadProgram,
  advancedSearchByKeyword,
  combinePrograms,
  getAdminPrograms,
  getProgramDepartments,
  getOrganizationTypes,
  getProgramsWithScores,
  getTeamReconciliationPrograms,
  getProgramWithScoringInfo,
  getDepartmentReconciliationPrograms,
  getFinalReviewPrograms,
  getProgramScoresByResult,
  getProgramsForInsightsTable,
  getFullProgramInfo,
  getProgramCountForBudget,
  adjustServiceTypeBoundaries,
  filterByBPAScore,
  filterByResultScore,
  getScoredServiceTypePrograms,
  getDepartmentsAndDivisions,
  filterByActiveCostCenters,
  filterByUserAssignedCostCenters,
  getCostDetails,
  getDefaultServiceTypeBoundaries,
  getScoringPredictions,
  getRxAdminScoringPredictions,
  getPredictedPrograms,
  finalizeRxAdminScores,
  analyzePrograms,
  getCostBenchmarkAnalysis,
  getSimilarPrograms,
  getProgramInfoForInsightPrediction,
  getAIAnalysis,
};
